import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
              <Link to="/category/news" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
                返回资讯列表
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'hth官方平台',
    },
    url: `https://www.hth-platform.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.hth-platform.com/article/${article.slug}`,
    },
  };

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <ScrollToTop />
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="bg-background-100">
              <div className="w-full px-4 md:px-6 py-10 md:py-14">
                <div className="max-w-3xl mx-auto">
                  <div className="mb-4">
                    <Breadcrumb
                      items={[
                        { label: '首页', path: '/' },
                        { label: article.category, path: `/category/${article.category === '平台资讯' ? 'news' : 'guide'}` },
                        { label: article.title },
                      ]}
                    />
                  </div>

                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                    {article.title}
                  </h1>

                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-price-tag-3-line"></i>
                      {article.category}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-accent-50 text-accent-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full px-4 md:px-6 py-10">
              <div className="max-w-3xl mx-auto">
                <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-8">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                    [&_h4]:text-lg [&_h4]:md:text-xl [&_h4]:font-heading [&_h4]:font-semibold [&_h4]:text-foreground-900 [&_h4]:mt-8 [&_h4]:mb-3
                    [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base
                    [&_strong]:text-foreground-900
                    [&_br]:block [&_br]:mb-2"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                ></div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2">
                        {ra.description}
                      </p>
                      <div className="mt-2 text-xs text-foreground-400">{ra.date}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}